import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import PageLayout from '../../components/layouts/PageLayout';
import {
  CriticalResultsAlertsListPanel,
  CriticalResultsFilterBar,
  CriticalResultsRefreshBanner,
  CriticalNotificationDetailModal,
} from '../../components/criticalResultsDashboard';
import { authService } from '../../auth/authService';
import {
  CRITICAL_RESULTS_DRILL_DOWN_BY_SLUG,
  CRITICAL_RESULTS_DEFAULT_REFRESH_INTERVAL_SEC,
  CRITICAL_RESULTS_EXPORT_DATASET_BY_SLUG,
  CRITICAL_RESULTS_LIST_PAGE_SIZE,
} from '../../constants/criticalResultsDashboard.constants';
import {
  useCriticalResultsFilters,
  useCriticalResultsListParams,
} from '../../hooks/useCriticalResultsFilters';
import {
  aggregateRefreshWarning,
  buildDepartmentOptions,
  buildReferringClinicianOptions,
  useCriticalResultsAutoRefresh,
  useCriticalResultsDefaultFacilityId,
  useCriticalResultsFacilities,
  useCriticalResultsNotifyWarning,
  useCriticalResultsSection,
  useCriticalResultsSuperAdmin,
} from '../../hooks/useCriticalResultsPageSetup';
import { useCriticalResultsExport } from '../../hooks/useCriticalResultsExport';
import {
  getCriticalResultsSummary,
  listCriticalResultsAlerts,
} from '../../services/criticalResultsDashboardService';
import type {
  CriticalResultsDashboardAlert,
  CriticalResultsDashboardSummary,
  CriticalResultsExportDataset,
  CriticalResultsExportFormat,
} from '../../types/criticalResultsDashboard.types';
import type { CriticalNotificationDetailContext } from '../../types/criticalNotification.types';
import { buildDetailContextFromAlert } from '../../utils/criticalNotificationUtils';
import { mergeLastUpdated } from '../../utils/criticalResultsRefreshUtils';
import { getExpectedKpiCount } from '../../utils/criticalResultsKpiUtils';
import { formatLastUpdatedLabel } from '../../utils/criticalResultsDashboardUtils';
import { canAccessCriticalResultsDashboard } from '../../utils/permissions';
import { getDefaultRoute } from '../../utils/getDefaultRoute';
import { ICON_STROKE_WIDTH } from '../../constants';
import './CriticalResultsDashboard.css';

interface AlertsRefreshPayload {
  rows: CriticalResultsDashboardAlert[];
  count: number;
}

export default function CriticalResultsAlertsDrillDown() {
  const { categorySlug = '' } = useParams<{ categorySlug: string }>();
  const config = CRITICAL_RESULTS_DRILL_DOWN_BY_SLUG[categorySlug];
  const user = authService.getUser();
  const isSuperAdmin = useCriticalResultsSuperAdmin();
  const defaultFacilityId = useCriticalResultsDefaultFacilityId();
  const { notifyWarning, scheduleRetryRef, uiNotificationService } =
    useCriticalResultsNotifyWarning();
  const [detailContext, setDetailContext] = useState<CriticalNotificationDetailContext | null>(
    null
  );
  const [detailOpen, setDetailOpen] = useState(false);

  const handleAlertSelect = useCallback((alert: CriticalResultsDashboardAlert) => {
    setDetailContext(buildDetailContextFromAlert(alert));
    setDetailOpen(true);
  }, []);

  const handleDetailClose = useCallback(() => {
    setDetailOpen(false);
    setDetailContext(null);
  }, []);

  const { filters, apiFilters, updateFilters, clearFilters, buildDrillDownSearch } =
    useCriticalResultsFilters(defaultFacilityId);

  const { listState, updateListState, pageSize } = useCriticalResultsListParams({
    sortBy: config?.defaultSortBy ?? 'startedAt',
    sortOrder: config?.defaultSortOrder ?? 'DESC',
  });

  const {
    exporting,
    exportMenuOpen,
    toggleExportMenu,
    handleExport: runExport,
  } = useCriticalResultsExport(uiNotificationService);

  const { facilitySelectOptions } = useCriticalResultsFacilities(
    isSuperAdmin,
    filters.facilityId,
    updateFilters
  );

  const listQueryKey = useMemo(
    () =>
      JSON.stringify({
        page: listState.page,
        sortBy: listState.sortBy,
        sortOrder: listState.sortOrder,
        search: listState.search.trim(),
      }),
    [listState.page, listState.sortBy, listState.sortOrder, listState.search]
  );

  const summarySection = useCriticalResultsSection<CriticalResultsDashboardSummary>({
    enabled: Boolean(apiFilters),
    notifyWarning,
    fetcher: async () => {
      if (!apiFilters) {
        return { success: false, error: 'Missing facility filters' };
      }
      const result = await getCriticalResultsSummary(apiFilters);
      return result.success && result.data
        ? { success: true, data: result.data }
        : { success: false, error: result.error || 'Failed to load filter metadata' };
    },
  });

  const alertsSection = useCriticalResultsSection<AlertsRefreshPayload>({
    enabled: Boolean(apiFilters && config),
    notifyWarning,
    fetcher: async () => {
      if (!apiFilters || !config) {
        return { success: false, error: 'Missing alert filters' };
      }
      const result = await listCriticalResultsAlerts(config.category, {
        ...apiFilters,
        search: listState.search.trim() || undefined,
        limit: CRITICAL_RESULTS_LIST_PAGE_SIZE,
        offset: (listState.page - 1) * CRITICAL_RESULTS_LIST_PAGE_SIZE,
        sortBy: listState.sortBy,
        sortOrder: listState.sortOrder,
      });
      return result.success
        ? { success: true, data: { rows: result.data, count: result.count } }
        : { success: false, error: result.error || 'Failed to load alerts' };
    },
  });

  const summary = summarySection.data;
  const alerts = alertsSection.data?.rows ?? [];
  const alertCount = alertsSection.data?.count ?? 0;
  const apiFiltersKey = useMemo(() => JSON.stringify(apiFilters), [apiFilters]);
  const searchActive = listState.search.trim().length > 0;
  const expectedKpiCount = getExpectedKpiCount(summary, config?.kpiKey);

  const handleAcknowledged = useCallback(() => {
    void summarySection.refresh('manual');
    void alertsSection.refresh('manual');
  }, [summarySection, alertsSection]);

  useCriticalResultsAutoRefresh(
    filters.autoRefresh && Boolean(apiFilters),
    CRITICAL_RESULTS_DEFAULT_REFRESH_INTERVAL_SEC,
    trigger => {
      void alertsSection.refresh(trigger);
    },
    scheduleRetryRef
  );

  useEffect(() => {
    if (!apiFilters) {
      return;
    }
    void summarySection.refresh('initial');
    void alertsSection.refresh('initial');
  }, [apiFiltersKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!apiFilters || !config) {
      return;
    }
    void alertsSection.refresh('initial');
  }, [listQueryKey, config?.category]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleManualRefresh = useCallback(() => {
    void summarySection.refresh('manual');
    void alertsSection.refresh('manual');
  }, [summarySection, alertsSection]);

  const breadcrumbs = [
    { label: 'Home', path: '/' },
    { label: 'Dashboard', path: '/admin-dashboard' },
    { label: 'Analytics' },
    { label: 'Criticality Results', path: `/critical-results-dashboard${buildDrillDownSearch()}` },
    { label: config?.title || 'Alerts' },
  ];

  const departmentOptions = useMemo(
    () => buildDepartmentOptions(summary?.departmentSummary),
    [summary?.departmentSummary]
  );

  const referringClinicianOptions = useMemo(
    () =>
      buildReferringClinicianOptions(
        summary?.referringClinicianSummary,
        filters.referringClinician
      ),
    [summary?.referringClinicianSummary, filters.referringClinician]
  );

  const lastUpdated = useMemo(
    () => mergeLastUpdated([summarySection.lastUpdated, alertsSection.lastUpdated]),
    [summarySection.lastUpdated, alertsSection.lastUpdated]
  );

  const refreshWarning = useMemo(
    () => aggregateRefreshWarning([summarySection, alertsSection]),
    [summarySection, alertsSection]
  );

  const initialLoading = alertsSection.initialLoading;
  const refreshing = summarySection.refreshing || alertsSection.refreshing;

  const exportDataset = (CRITICAL_RESULTS_EXPORT_DATASET_BY_SLUG[categorySlug] ||
    'open-alerts') as CriticalResultsExportDataset;

  const onExport = (format: CriticalResultsExportFormat) => {
    if (!apiFilters) {
      return;
    }
    runExport(format, exportDataset, apiFilters, {
      sortBy: listState.sortBy,
      sortOrder: listState.sortOrder,
      search: listState.search,
    });
  };

  if (!canAccessCriticalResultsDashboard(user)) {
    return (
      <Navigate
        to={getDefaultRoute(user)}
        replace
      />
    );
  }

  if (!config) {
    return (
      <Navigate
        to={`/critical-results-dashboard${buildDrillDownSearch()}`}
        replace
      />
    );
  }

  if (!defaultFacilityId && !isSuperAdmin) {
    return (
      <PageLayout
        title={config.title}
        breadcrumbs={breadcrumbs}
      >
        <p className="crd-message">Your account is not associated with a facility.</p>
      </PageLayout>
    );
  }

  if (!filters.facilityId) {
    return (
      <PageLayout
        title={config.title}
        breadcrumbs={breadcrumbs}
      >
        <p className="crd-message">
          {isSuperAdmin
            ? 'Select a facility to view critical results alerts.'
            : 'Your account is not associated with a facility.'}
        </p>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title={config.title}
      hideTitleInHeader
      breadcrumbs={breadcrumbs}
    >
      <div className="crd-dashboard">
        <Link
          to={`/critical-results-dashboard${buildDrillDownSearch()}`}
          className="crd-back-link"
        >
          <ArrowLeft
            size={16}
            strokeWidth={ICON_STROKE_WIDTH}
          />
          Back to dashboard
        </Link>

        <CriticalResultsFilterBar
          startDate={filters.startDate}
          endDate={filters.endDate}
          onStartDateChange={value => updateFilters({ startDate: value }, { resetPage: true })}
          onEndDateChange={value => updateFilters({ endDate: value }, { resetPage: true })}
          facilityOptions={facilitySelectOptions}
          facilityId={filters.facilityId}
          onFacilityChange={value => updateFilters({ facilityId: value }, { resetPage: true })}
          showFacilityFilter={isSuperAdmin}
          department={filters.department}
          departmentOptions={departmentOptions}
          onDepartmentChange={value => updateFilters({ department: value }, { resetPage: true })}
          criticalityBand={filters.criticalityBand}
          onCriticalityBandChange={value =>
            updateFilters({ criticalityBand: value }, { resetPage: true })
          }
          status={filters.status}
          onStatusChange={value => updateFilters({ status: value }, { resetPage: true })}
          escalationLevel={filters.escalationLevel}
          onEscalationLevelChange={value =>
            updateFilters({ escalationLevel: value }, { resetPage: true })
          }
          referringClinician={filters.referringClinician}
          referringClinicianOptions={referringClinicianOptions}
          onReferringClinicianChange={value =>
            updateFilters({ referringClinician: value }, { resetPage: true })
          }
          autoRefresh={filters.autoRefresh}
          onAutoRefreshChange={value => updateFilters({ autoRefresh: value })}
          lastRefreshedLabel={formatLastUpdatedLabel(lastUpdated)}
          loading={initialLoading}
          refreshing={refreshing}
          exporting={exporting}
          exportMenuOpen={exportMenuOpen}
          onExportMenuToggle={toggleExportMenu}
          onExport={onExport}
          onClear={clearFilters}
          onRefresh={handleManualRefresh}
        />

        <CriticalResultsRefreshBanner
          message={refreshWarning}
          onRetry={handleManualRefresh}
        />

        <CriticalResultsAlertsListPanel
          title={config.title}
          alerts={alerts}
          totalCount={alertCount}
          expectedKpiCount={expectedKpiCount}
          searchActive={searchActive}
          currentPage={listState.page}
          pageSize={pageSize}
          loading={initialLoading}
          refreshing={refreshing}
          error={alertsSection.warning}
          search={listState.search}
          sortBy={listState.sortBy}
          sortOrder={listState.sortOrder}
          onSearchChange={value => updateListState({ search: value, page: 1 })}
          onSortChange={(sortBy, sortOrder) => updateListState({ sortBy, sortOrder, page: 1 })}
          onPageChange={page => updateListState({ page })}
          onAlertSelect={handleAlertSelect}
        />

        <CriticalNotificationDetailModal
          open={detailOpen}
          context={detailContext}
          onClose={handleDetailClose}
          onAcknowledged={handleAcknowledged}
        />
      </div>
    </PageLayout>
  );
}
